import PropTypes from "prop-types";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { withRouter } from "react-router-dom";
import { useTranslation } from "react-i18next";
import OccurrencesHeader from "../../components/occurrences/OccurrencesHeader";
import TeacherService from "../../services/TeacherService";
import OccurrencesPage from "../../components/occurrences/OccurrencesPage";
import { isIndividualOccurrenceJob } from "../../utils/jobHelpers";
import withOccurrenceAdvanced from "../../utils/withOccurrenceAdvanced";
import OccurrenceTypeFilters, {
  ALL_REASONS_ID
} from "../../components/occurrences/OccurrenceTypeFilters";
import useOccurrenceTypeFilters from "../../components/occurrences/useOccurrenceTypeFilters";

const findClassName = async classId => {
  try {
    const result = await TeacherService.getClasses({ search: classId });
    const list = Array.isArray(result) ? result : (result && result.items) || [];
    const found = list.find(item => String(item.id) === String(classId));
    return (found && found.name) || classId;
  } catch (err) {
    return classId;
  }
};

function ClassOccurrencesAdvanced({ match, occurrenceAdvancedAcl }) {
  const { t } = useTranslation();
  const classId = match.params.id;
  const [className, setClassName] = useState("");
  const [students, setStudents] = useState([]);
  const typeFilters = useOccurrenceTypeFilters(classId);
  const { selectedGroup, reasonId } = typeFilters;

  useEffect(() => {
    findClassName(classId).then(setClassName);
    TeacherService.getStudentsForClass(classId)
      .then(list => setStudents(Array.isArray(list) ? list : []))
      .catch(() => setStudents([]));
  }, [classId]);

  const fetchOccurrences = useCallback(
    year => TeacherService.getClassOccurrenceHistory(classId, year),
    [classId]
  );

  const occurrenceFilter = useMemo(() => {
    if (!selectedGroup) {
      return null;
    }
    if (reasonId !== ALL_REASONS_ID) {
      return occurrence =>
        `${occurrence.idType}-${occurrence.idGroup}` === reasonId;
    }
    return occurrence =>
      String(occurrence.idGroup) === String(selectedGroup.idGroup);
  }, [selectedGroup, reasonId]);

  const matchesJob = useCallback(
    job => isIndividualOccurrenceJob(job, classId),
    [classId]
  );

  const studentOptions = useMemo(
    () =>
      students.map(item => ({
        ...item,
        classId: item.classId || classId,
        className: item.className || className || classId
      })),
    [students, classId, className]
  );

  return (
    <OccurrencesPage
      classId={classId}
      backTo={`/classes/${classId}`}
      header={(
        <OccurrencesHeader
          name={className || classId}
          info={t("occurrences.advanced.classTitle")}
        />
      )}
      filters={<OccurrenceTypeFilters {...typeFilters} />}
      fetchOccurrences={fetchOccurrences}
      occurrenceFilter={occurrenceFilter}
      matchesJob={matchesJob}
      advanced
      searchScope="class"
      canView={occurrenceAdvancedAcl.view}
      canCreate={occurrenceAdvancedAcl.create}
      addLabel={t("occurrences.advanced.add")}
      students={studentOptions}
    />
  );
}

ClassOccurrencesAdvanced.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.objectOf(PropTypes.string)
  }).isRequired,
  occurrenceAdvancedAcl: PropTypes.shape({
    view: PropTypes.bool,
    create: PropTypes.bool,
    edit: PropTypes.bool,
    delete: PropTypes.bool
  })
};

ClassOccurrencesAdvanced.defaultProps = {
  occurrenceAdvancedAcl: {
    view: false,
    create: false,
    edit: false,
    delete: false
  }
};

export default withOccurrenceAdvanced(withRouter(ClassOccurrencesAdvanced));
